"use client"

import type { FullTranslation } from "../lib/i18n"
import { useAutoleadsLocale } from "../lib/locale-context"
import { TIERS } from "../lib/pricing"
import {
  COMPARISON_GROUPS,
  type ComparisonCell,
} from "../lib/pricing-comparison"
import { useTr } from "../lib/use-tr"
import { Check, ChevronDown, Minus } from "lucide-react"
import { useId, useState } from "react"

import { cn } from "@/lib/utils"

import type { AutoLeadsCatalogTier } from "@/nango/types/apps.autoleads/autoleads/AutoLeads"

// The middle tier is the recommended one on the cards above; the table tints
// the matching column so the eye lands in the same place.
const HIGHLIGHTED_TIER: AutoLeadsCatalogTier = "growth"

const COMPARISON_T = {
  toggle_open: {
    FR: "Comparer les offres en détail",
    EN: "Compare plans in detail",
  },
  toggle_close: {
    FR: "Masquer la comparaison",
    EN: "Hide the comparison",
  },
  toggle_hint: {
    FR: "Ligne par ligne, les trois offres côte à côte",
    EN: "Line by line, the three plans side by side",
  },
  caption: {
    FR: "Comparaison détaillée des offres Starter, Growth et Scale",
    EN: "Detailed comparison of the Starter, Growth and Scale plans",
  },
  feature_column: { FR: "Détail", EN: "Detail" },
  per_month: { FR: "/ mois", EN: "/ mo" },
  included: { FR: "Inclus", EN: "Included" },
  not_included: { FR: "Non inclus", EN: "Not included" },
} satisfies FullTranslation

function CellValue({
  cell,
  yesLabel,
  noLabel,
  locale,
}: {
  cell: ComparisonCell
  yesLabel: string
  noLabel: string
  locale: "FR" | "EN"
}) {
  if (cell === true) {
    return (
      <>
        <Check className="text-primary-cta size-4" aria-hidden />
        <span className="sr-only">{yesLabel}</span>
      </>
    )
  }
  if (cell === false) {
    return (
      <>
        <Minus className="text-muted-foreground/50 size-4" aria-hidden />
        <span className="sr-only">{noLabel}</span>
      </>
    )
  }
  return <span className="text-foreground">{cell[locale]}</span>
}

export function PricingComparison() {
  const tr = useTr(COMPARISON_T)
  const locale = useAutoleadsLocale()
  const [open, setOpen] = useState(false)
  const panelId = useId()

  return (
    <div className="mt-10">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((previous) => !previous)}
        className="border-border/70 bg-card/40 hover:bg-card focus-visible:ring-primary-cta/40 group flex w-full items-center justify-between gap-4 rounded-xl border px-5 py-4 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        <span className="min-w-0">
          <span className="text-foreground block text-sm font-medium">
            {open ? tr("toggle_close") : tr("toggle_open")}
          </span>
          <span className="text-muted-foreground text-2sm mt-0.5 block">
            {tr("toggle_hint")}
          </span>
        </span>
        <ChevronDown
          className={cn(
            "text-muted-foreground group-hover:text-foreground size-4 shrink-0 transition-transform duration-300 ease-out motion-reduce:transition-none",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      {/* 0fr -> 1fr animates to the content's natural height without measuring
          it in JS, and without animating `height` itself. */}
      <div
        id={panelId}
        inert={!open}
        className="grid transition-[grid-template-rows] duration-500 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
        style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
      >
        <div className="overflow-hidden">
          <div
            className={cn(
              "transition-[opacity,translate] duration-300 ease-out motion-reduce:transition-none",
              open
                ? "translate-y-0 opacity-100 delay-150"
                : "-translate-y-1 opacity-0",
            )}
          >
            {/* Narrow screens scroll the table sideways rather than lose
                columns; the label column stays pinned so rows stay readable. */}
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-136 border-collapse text-left">
                <caption className="sr-only">{tr("caption")}</caption>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      className="bg-background text-muted-foreground sticky left-0 z-10 py-3 pr-4 text-xs font-medium"
                    >
                      {tr("feature_column")}
                    </th>
                    {TIERS.map((tier) => (
                      <th
                        key={tier.tier}
                        scope="col"
                        className={cn(
                          "px-4 py-3 align-bottom",
                          tier.tier === HIGHLIGHTED_TIER && "bg-primary-cta/5",
                        )}
                      >
                        <span className="text-foreground font-display text-md tracking-snug block font-semibold">
                          {tier.label}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>

                {COMPARISON_GROUPS.map((group) => (
                  <tbody key={group.key}>
                    {/* Split into real cells rather than one colSpan header,
                        so the highlighted column reads as one continuous band
                        instead of breaking at every group. */}
                    <tr>
                      <th
                        scope="rowgroup"
                        className="border-border/60 bg-background text-muted-foreground text-2xs tracking-wider-caps sticky left-0 z-10 border-t pt-6 pr-4 pb-2 font-semibold uppercase"
                      >
                        {group.title[locale]}
                      </th>
                      {TIERS.map((tier) => (
                        <td
                          key={tier.tier}
                          className={cn(
                            "border-border/60 border-t",
                            tier.tier === HIGHLIGHTED_TIER &&
                              "bg-primary-cta/5",
                          )}
                        />
                      ))}
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row.key} className="group/row">
                        <th
                          scope="row"
                          className="border-border/40 bg-background text-muted-foreground text-2sm sticky left-0 z-10 border-t py-3 pr-4 font-normal"
                        >
                          {row.label[locale]}
                        </th>
                        {TIERS.map((tier) => (
                          <td
                            key={tier.tier}
                            className={cn(
                              "border-border/40 text-2sm border-t px-4 py-3 tabular-nums",
                              tier.tier === HIGHLIGHTED_TIER &&
                                "bg-primary-cta/5",
                            )}
                          >
                            {row.derive ? (
                              <span className="text-foreground">
                                {row.derive(tier)}
                                {row.key === "price" ? (
                                  <span className="text-muted-foreground ml-1 text-xs">
                                    {tr("per_month")}
                                  </span>
                                ) : null}
                              </span>
                            ) : row.values ? (
                              <CellValue
                                cell={row.values[tier.tier]}
                                yesLabel={tr("included")}
                                noLabel={tr("not_included")}
                                locale={locale}
                              />
                            ) : null}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                ))}
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
